import { useState, useCallback } from "react";
import { useTurnstile } from "../../hooks/useTurnstile";

function ContactForm() {
  const sitekey = process.env.REACT_APP_TURNSTILE_SITEKEY_PRODUCTION;

  const [turnstileToken, setTurnstileToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");

  // Turnstile callbacks (Turnstile calls these)
  const onSuccess = useCallback((token) => {
    // Optional: log token lifecycle for debugging
    // console.log("[Turnstile] onSuccess:", token?.slice(0, 12), "…");
    setTurnstileToken(token || "");
  }, []);

  const onExpired = useCallback(() => {
    // console.warn("[Turnstile] onExpired");
    setTurnstileToken("");
  }, []);

  const onError = useCallback(() => {
    // console.error("[Turnstile] onError");
    setTurnstileToken("");
  }, []);

  // Hook renders widget into a div via ref and returns helpers to reset
  const { turnstileRef, reset } = useTurnstile(sitekey, {
    onSuccess,
    onExpired,
    onError,
  });

  const handleSubmit = async (e) => {
    e.preventDefault(); // ✅ prevents navigation

    // ✅ PATTERN A: capture the form element BEFORE any await (prevents pooled event nulls)
    const formEl = e.currentTarget;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");

    try {
      const formData = new FormData(formEl); // ✅ use captured formEl
      const name = formData.get("name");
      const email = formData.get("email");
      const role = formData.get("role");
      const message = formData.get("message");

      if (!turnstileToken) {
        setSubmitStatus("error");
        setSubmitMessage("Please complete the CAPTCHA verification");
        return;
      }

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          role,
          message,
          token: turnstileToken,
        }),
      });

      // ✅ More resilient parsing: try JSON, fall back to text if needed
      let result;
      let rawText = "";
      try {
        result = await response.json();
      } catch {
        rawText = await response.text().catch(() => "");
        result = {};
      }

      if (response.ok && result.success) {
        setSubmitStatus("success");
        setSubmitMessage(
          result.message || "Thank you! Your message has been sent."
        );

        // ✅ Use the captured form element (Pattern A fix)
        formEl.reset();

        setTurnstileToken("");

        // Reset turnstile widget after a brief delay to avoid race conditions
        setTimeout(() => {
          reset?.();
        }, 100);
      } else {
        // Preserve your existing UX but provide better diagnostics if desired
        const msg =
          result?.error ||
          (rawText ? `Server responded with: ${rawText.slice(0, 200)}…` : "") ||
          "Failed to send message. Please try again.";

        setSubmitStatus("error");
        setSubmitMessage(msg);

        setTurnstileToken("");

        // Optionally: reset only on certain Turnstile error codes (e.g., timeout-or-duplicate)
        setTimeout(() => {
          reset?.();
        }, 100);
      }
    } catch (error) {
      // This catch will now only fire on network/CORS/runtime errors—not on the previous reset crash
      console.error("Form submission error:", error);
      setSubmitStatus("error");
      setSubmitMessage("An error occurred. Please try again later.");
      setTurnstileToken("");

      // It’s typically safer not to reset on network errors; keeping your behavior as-is:
      setTimeout(() => {
        reset?.();
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-16 bg-white/50 rounded-lg border border-black/10 p-6 md:p-8"
    >
      <div className="space-y-6">
        <div>
          <label
            htmlFor="name"
            className="block font-body text-sm font-semibold text-hero-foreground mb-2"
          >
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            disabled={isSubmitting}
            className="w-full px-4 py-3 border border-black/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-geo-primary focus:border-transparent transition-all disabled:opacity-50"
            placeholder="Your name"
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block font-body text-sm font-semibold text-hero-foreground mb-2"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            disabled={isSubmitting}
            className="w-full px-4 py-3 border border-black/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-geo-primary focus:border-transparent transition-all disabled:opacity-50"
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label
            htmlFor="role"
            className="block font-body text-sm font-semibold text-hero-foreground mb-2"
          >
            Role/Subject *
          </label>
          <input
            type="text"
            id="role"
            name="role"
            required
            disabled={isSubmitting}
            className="w-full px-4 py-3 border border-black/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-geo-primary focus:border-transparent transition-all disabled:opacity-50"
            placeholder="e.g., DevOps Engineer position"
          />
        </div>

        <div>
          <label
            htmlFor="message"
            className="block font-body text-sm font-semibold text-hero-foreground mb-2"
          >
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            disabled={isSubmitting}
            rows="6"
            className="w-full px-4 py-3 border border-black/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-geo-primary focus:border-transparent transition-all resize-none disabled:opacity-50"
            placeholder="Your message..."
          />
        </div>

        {/* Turnstile */}
        <div className="flex justify-center">
          <div ref={turnstileRef} />
        </div>

        {/* Status Message */}
        {submitStatus && (
          <div
            className={`p-4 rounded-lg text-center font-body ${
              submitStatus === "success"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {submitMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-8 py-3 bg-hero-foreground text-hero-background font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Sending..." : "Send Message"}
        </button>
      </div>
    </form>
  );
}

export default ContactForm;
